import { makeAutoObservable } from "mobx";
import { RemoteData } from "src/common/RemoteData";
import { ZUid } from "src/types/zUid";
import { cvtError, onError } from "src/common/onError";
import { debounce, DebounceCounter } from "src/common/debounce";
import { msgQueueStore } from "src/components/MsgQueue";
import { t } from "i18next";
import {
  calculatePlanning2,
  createPlanning2TaskFromTemplate,
  loadPlanning2Settings,
  loadPlanning2TableSettings,
} from "../apiPlanning2";
import { ZPlanning2Row } from "../ZPlanning2Row";
import { Planning2TableStore } from "./Planning2Table/Planning2TableStore";
import { ZPlanning2Settings } from "../ZPlanning2Settings";
import { Planning2DatesStore } from "./Planning2Dates/Planning2DatesStore";
import { Planning2GanttStore } from "./Planning2Gantt/Planning2GanttStore";
import { ZBadCalculatePlanning2 } from "../ZCalculatePlanning2";

export type Planning2ControlData = {
  tableStore: Planning2TableStore;
  gantt: Planning2GanttStore;
  settings: ZPlanning2Settings;
};

type Mode = "delete" | "newTask" | "newTemplate";

export const planning2ControlStore = makeAutoObservable({
  /**
   * @deprecated Переключатель между старым и новым компонентом таблицы. Убрать после удаления компонента Planning2Table
   */
  useGantt: true,
  setUseGantt(value: boolean) {
    this.useGantt = value;
  },
  async init(objectServiceId: ZUid, entityId: ZUid) {
    this.objectServiceId = objectServiceId;
    this.entityId = entityId;
    this.setData({ status: "wait" });
    try {
      const settings = await loadPlanning2Settings(objectServiceId);
      const tableSettings = await loadPlanning2TableSettings();
      const datesStore = new Planning2DatesStore({
        reversed: !!settings.planDateReversedCalculation,
      });
      this.setDatesStore(datesStore);
      const tableStore = new Planning2TableStore(
        objectServiceId,
        entityId,
        tableSettings,
        datesStore,
      );
      const gantt = new Planning2GanttStore(tableStore, datesStore);
      const result = { tableStore, settings, gantt };
      this.setData({ status: "ready", result });
    } catch (e) {
      this.setData({ status: "error", error: cvtError(e) });
    }
  },
  datesStore: new Planning2DatesStore(),
  setDatesStore(store: Planning2DatesStore) {
    this.datesStore = store;
  },
  objectServiceId: "" as ZUid,
  entityId: "" as ZUid,
  mode: null as Mode | null,
  setMode(newMode: Mode | null) {
    this.mode = newMode;
  },
  data: { status: "none" } as RemoteData<Planning2ControlData>,
  setData(newData: RemoteData<Planning2ControlData>) {
    this.data = newData;
  },
  get toolsDisabled(): boolean {
    return this.data.status !== "ready";
  },
  get searchDisabled(): boolean {
    return this.toolsDisabled;
  },
  get selectedTasks(): ZPlanning2Row[] {
    if (this.data.status !== "ready") return [];
    const { tableStore } = this.data.result;
    return tableStore.selectedTasksList;
  },
  get addDisabled(): boolean {
    return this.toolsDisabled;
  },

  // Delete
  get deleteDisabled(): boolean {
    if (this.toolsDisabled) {
      return true;
    }
    return this.selectedTasks.length === 0;
  },
  get deleteTitle(): string | undefined {
    return this.selectedTasks.length === 0
      ? "Не выбрано ни одной задачи"
      : undefined;
  },
  isDeleting: false,
  setIsDeleting(flag: boolean) {
    this.isDeleting = flag;
  },
  doDelete() {
    const { data } = this;
    if (data.status === "ready") {
      const { tableStore } = data.result;
      this.setIsDeleting(true);
      const count = this.selectedTasks.length;
      tableStore
        .deleteSelectedTasks()
        .then(() => this.setMode(null))
        .then(() => this.showDeleteMessage(count))
        .finally(() => this.setIsDeleting(false))
        .catch(onError);
    }
  },

  showDeleteMessage(count = 1) {
    msgQueueStore.add({
      type: "success",
      message: t("Task deletion completed", { count }),
      description: t("Task related task bindings have been removed", { count }),
    });
  },

  get settings(): ZPlanning2Settings | undefined {
    const { data } = this;
    if (data.status !== "ready") return undefined;
    return data.result.settings;
  },

  reload() {
    const { data } = this;
    if (data.status === "ready") {
      data.result.tableStore.reload().catch(onError);
    }
  },

  calculating: false,
  setCalculating(on: boolean) {
    this.calculating = on;
  },
  get canRecalcDates(): boolean {
    return !this.msgRecalcDates;
  },
  get msgRecalcDates(): string {
    if (this.data.status !== "ready") {
      return "Данные не загружены";
    }
    const { length } = this.data.result.tableStore.allTasks;
    if (!length) return "Нет задач";
    const { reversed, begin, end } = this.datesStore;
    if (reversed) {
      if (!end) return "Не заполнена дата окончания";
    } else if (!begin) return "Не заполнена дата начала";
    return "";
  },
  recalcDates() {
    const { begin, end } = this.datesStore;
    const date = this.datesStore.reversed ? end : begin;
    this.setCalculating(true);
    // на время выполнения запроса ставим текущие значения, чтобы исчезли сообщения об ошибках в полях дат
    // после выполнения внутри reload они проставятся уже из ответа сервера.
    this.datesStore.setInitialDates(begin, end);
    calculatePlanning2(this.objectServiceId, this.entityId, date)
      .finally(() => this.setCalculating(false))
      .then((result) => {
        this.reload();
        if (result) {
          this.setBadCalc(result);
        }
      })
      .catch(onError);
  },

  badCalc: null as ZBadCalculatePlanning2 | null,
  setBadCalc(data: ZBadCalculatePlanning2 | null | undefined) {
    this.badCalc = data ?? null;
  },

  onTaskFinish() {
    this.setMode(null);
    this.reload();
  },

  onTemplateFinish(templateEntityId: ZUid) {
    const { objectServiceId, entityId } = this;
    const task = async () => {
      this.setMode(null);
      await createPlanning2TaskFromTemplate({
        templateEntityId,
        objectServiceId,
        entityId,
      });
      this.reload();
    };
    task().catch(onError);
  },

  search: "",
  setSearch(newSearch: string) {
    this.search = newSearch;
    const { data } = this;
    debounce(searchCounter, 200, () => {
      if (data.status === "ready") {
        data.result.tableStore.setFilter(newSearch);
      }
    });
  },

  get tableStore(): Planning2TableStore | undefined {
    const { data } = this;
    return data.status === "ready" ? data.result.tableStore : undefined;
  },
});

const searchCounter: DebounceCounter = {};
